// ============================================================================
// PairsForm — Référentiel de certification RS 7137 (SOURCE UNIQUE)
// ============================================================================
// @systeme: certification-rs7137
// « Animer des groupes de Codéveloppement professionnel et managérial »,
// enregistré à France Compétences (renouvellement 2025 de l'ancienne RS 6136).
// Documents de référence : référentiel d'activités, compétences et évaluation
// + modalités d'organisation et de contrôle des épreuves (Anima Cecodev, 2025).
//
// Avant 2026-09-08 ce référentiel vivait en CINQ copies divergentes (PV,
// correcteur, service de bilans, prompt IA, page jury). Toute évolution du
// référentiel officiel se fait ICI et nulle part ailleurs.
//
// Trois usages, un seul objet :
//   - la GRILLE de délibération (compétences × critères observables) ;
//   - le TABLEAU des compétences imprimé dans le bilan du candidat ;
//   - la PROSE injectée dans les prompts ({{referentiel}}).
// Depuis le lot 4 (2026-10-06), ce référentiel est la version D'ORIGINE du
// document `competences_vert` (table referentiels_codev, éditable par le
// pilote Cecodev dans « Savoir métier Codev ») ; les trois usages lisent le
// référentiel RÉSOLU de la session (`referentiel-competences.ts`), jamais
// cette constante directement.
// ============================================================================
export const COMPETENCES_RS7137 = [
    {
        code: "C1",
        nom: "Établir le cadre sécurisant",
        nomCourt: "Cadre sécurisant",
        definition: "Établir un cadre sécurisant en énonçant explicitement les règles de fonctionnement, en précisant les rôles et en garantissant des échanges respectueux de tous, y compris les participants en situation de handicap.",
        epreuves: ["SEANCE", "ENTRETIEN", "CAS_PRATIQUE"],
        criteres: [
            {
                code: "OC1_1",
                label: "Positionnement de la démarche",
                description: "Le candidat explique de manière structurée la démarche, ses bénéfices attendus et ses limites avant de commencer la séance.",
            },
            {
                code: "OC1_2",
                label: "Engagement sur les règles",
                description: "Le candidat énonce explicitement les 4 règles, leur sens, et recueille verbalement l'accord de chaque participant sur celles-ci.",
            },
            {
                code: "OC1_3",
                label: "Protection du cadre",
                description: "Le candidat pointe et recadre avec diplomatie et clarté le non-respect des consignes ou des règles par les participants.",
            },
        ],
    },
    {
        code: "C2",
        nom: "Conduire explicitement le processus méthodologique",
        nomCourt: "Processus méthodologique",
        definition: "Conduire avec sens et clarté les différentes étapes de la méthode, en adaptant avec inclusivité les consignes au groupe, en vérifiant leur appropriation et en guidant la progression séquentielle, pour déployer efficacement la démarche au sein d'un groupe de pairs.",
        epreuves: ["SEANCE", "ENTRETIEN", "CAS_PRATIQUE"],
        criteres: [
            {
                code: "OC2_1",
                label: "Clarté des consignes",
                description: "Pour chaque étape, le candidat expose de façon séquentielle : le sens (pourquoi), l'objectif (quoi), la procédure (comment) et la durée (combien de temps).",
            },
            {
                code: "OC2_2",
                label: "Maîtrise du temps",
                description: "Le candidat fait respecter la durée optimale de chaque étape, et ajuste avec pertinence lorsque nécessaire.",
            },
        ],
    },
    {
        code: "C3",
        nom: "Faciliter les interactions constructives",
        nomCourt: "Interactions constructives",
        definition: "Faciliter les échanges en encourageant l'expression équilibrée de chacun, en utilisant des techniques de facilitation adaptées, en régulant les dynamiques interpersonnelles et en valorisant la diversité des contributions, afin de créer un climat de confiance propice à l'implication active de tous les participants, y compris ceux en situation de handicap.",
        epreuves: ["SEANCE", "ENTRETIEN", "CAS_PRATIQUE"],
        criteres: [
            {
                code: "OC3_1",
                label: "Équilibrage de la participation",
                description: "Le candidat limite les participations excessives au sein du groupe et s'assure que chaque participant peut s'exprimer régulièrement pendant la séance.",
            },
            {
                code: "OC3_2",
                label: "Régulation des inconforts",
                description: "Le candidat repère et freine avec netteté les positions d'expert, à tendance psychologique, ou les implicites dévalorisants.",
            },
        ],
    },
    {
        code: "C4",
        nom: "Stimuler la réflexivité collective",
        nomCourt: "Réflexivité collective",
        definition: "Stimuler la prise de conscience des processus d'apprentissage en cours en mettant en lumière les moments significatifs, en questionnant les observations partagées, en valorisant les erreurs et en favorisant l'analyse distanciée, pour permettre au groupe d'identifier ses apprentissages individuels ou collectifs.",
        epreuves: ["SEANCE", "ENTRETIEN", "CAS_PRATIQUE"],
        criteres: [
            {
                code: "OC4_1",
                label: "Justesse de la posture",
                description: "Le candidat adopte une posture de facilitation, inclus dans le groupe de pairs et non-expert de la situation.",
            },
            {
                code: "OC4_2",
                label: "Valorisation de la divergence",
                description: "Le candidat invite le groupe à explorer largement (contexte, réflexion, action) et à être divergent dans sa production.",
            },
            {
                code: "OC4_3",
                label: "Objectif de la séance",
                description: "Le candidat clarifie dès le départ que l'objectif principal est l'apprentissage par l'appui à la réflexion, et non la résolution du problème, et obtient un contrat non limité à la solution.",
            },
            {
                code: "OC4_4",
                label: "Valorisation des opportunités",
                description: "Le candidat valorise en étape 6 les erreurs, imprévus et autres faits notables comme des opportunités d'apprentissage.",
            },
        ],
    },
    {
        code: "C5",
        nom: "Constituer une communauté apprenante",
        nomCourt: "Communauté apprenante",
        definition: "Constituer un groupe cohérent en analysant les besoins, en respectant les critères de complémentarité et de parité, en clarifiant les objectifs communs et en planifiant un cycle structuré de séances, afin de réunir les conditions d'une dynamique d'apprentissage entre pairs efficace et efficiente.",
        epreuves: ["CAS_PRATIQUE"],
        criteres: [
            {
                code: "OC5_1",
                label: "Conditions de constitution",
                description: "Le candidat énonce clairement les 3 critères de composition d'un groupe efficace et sait les appliquer dans un cas concret.",
            },
            {
                code: "OC5_2",
                label: "Conditions de déploiement",
                description: "Le candidat précise le nombre optimal de participants, les adaptations possibles pour les personnes en situation de handicap, définit le nombre recommandé de séances et leur fréquence idéale.",
            },
        ],
    },
];
export const LIBELLES_VERDICT_RS7137 = {
    ACQUIS: "Acquis",
    ACQUIS_AVEC_RESERVE: "Acquis avec réserve",
    NON_ACQUIS: "Non acquis",
    INDETERMINE: "Indéterminé",
};
// Les fonctions génériques (prose Markdown, tableau des compétences du
// bilan, verdict effectif) vivent dans `referentiel-competences.ts` (lot 4
// certifications, 2026-10-06) et prennent le référentiel de la SESSION en
// paramètre — ce fichier n'est plus que la DONNÉE du référentiel vert
// (RS 7137), version d'origine de `competences_vert` (referentiels_codev).
//# sourceMappingURL=referentiel-rs7137.js.map